/**
 * DSCR Workbook — REDESIGN. Built from the backend contract alone
 * (frontend_documentation/dscr-calculator-workbook.md + the four
 * `/pricing/dscr_calc*` endpoints), not from the existing tab.
 *
 * What the contract says the surface must convey, top to bottom:
 *
 *   1. The DSCR at three confidences — stated (face value) → validated (after
 *      UW haircuts) → verified (verified rows only). Equal tiers collapse.
 *   2. Whether the verified number clears the program floor (the lock gate),
 *      and how far it has moved from the loan's initial entry.
 *   3. The equation itself — qualifying income ÷ (expenses + P&I) — with the
 *      backend's numbers in it, so UW sees WHY the ratio is what it is.
 *   4. The evidence rows, organised the way the model organises them: rent
 *      rows belong to UNITS on subject properties (`unitId` / `rentBasis`),
 *      expense rows to the PROPERTY (`subjectPropertyId`). One grouped ledger,
 *      not two flat lists.
 *   5. What the documents on file can evidence (`/dscr_calc_evidence`) and
 *      which rows already use them — so an unattached lease is visible.
 *   6. The workbook's own change history (`changeHistory[]`), inline.
 *
 * EVERY NUMBER IS BACKEND-OWNED. `statedDscr` / `validatedDscr` /
 * `verifiedDscr` / `monthlyPiPayment` / `pitiaValidated` / `clearsFloor` /
 * `fullyVerified` / `componentSummary[]` and per-row `monthlyAmount` /
 * `validatedMonthlyAmount` / `isIncome` are computed fields; this tab only
 * sums leaf rows for group subtotals. Adding / editing a row goes through the
 * income and expense drawers, which POST the whole row set.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddOutlined from "@mui/icons-material/AddOutlined";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import SyncOutlined from "@mui/icons-material/SyncOutlined";
import VerifiedOutlined from "@mui/icons-material/VerifiedOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import isNil from "lodash/isNil";
import size from "lodash/size";
import { useSnackbar } from "notistack";

import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { dscrCalcApi } from "../../../../api/dscrCalcApi";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import {
  loanFetchedDataDscrCalcComponentsSet,
  loanFetchedDataDscrCalcEvidenceSet,
  loanFetchedDataDscrCalcSet,
} from "../../../../redux/reducers/loanFetchedDataReducer";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import { dscrPaymentBasisLabel } from "../../../../utils/loan/dscrPaymentBasis";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import {
  ActionCell,
  actionColumn,
} from "../../../admin/shared/adminTableActions";
import { amber, slate, status, teal } from "../../../../theme/colors";
import {
  GROSS_MONTHLY_RENT,
  isIncomeRow,
  leaseAboveMarket,
  listLoanUnits,
  propertyLabelOf,
  rentBasisLabel,
} from "../LoanDrilldownDscrCalculationsTab/constants";

/* ────────────────────────────── primitives ──────────────────────────────── */

const fmtX = (v) =>
  v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(2)}x`;
const when = (ts) => (ts ? formatDateTime(ts, "datetime") || String(ts) : "—");

const Panel = ({ title, hint, end, children, sx }) => (
  <Box
    sx={{
      border: `1px solid ${slate[200]}`,
      borderRadius: "12px",
      bgcolor: "#fff",
      overflow: "clip",
      ...sx,
    }}
  >
    {title ? (
      <Flx ac jb g={1} sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${slate[100]}` }}>
        <Flx ac g={0.75}>
          <Txt sx={{ fontSize: 13, fontWeight: 700, color: slate[900] }}>{title}</Txt>
          {hint ? (
            <Tooltip title={hint}>
              <InfoOutlined sx={{ fontSize: 14, color: slate[400] }} />
            </Tooltip>
          ) : null}
        </Flx>
        {end}
      </Flx>
    ) : null}
    {children}
  </Box>
);

const Eyebrow = ({ children }) => (
  <Txt
    sx={{
      display: "block",
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: ".08em",
      textTransform: "uppercase",
      color: slate[500],
    }}
  >
    {children}
  </Txt>
);

const Pill = ({ label, tone = "neutral", icon }) => {
  const t = status[tone] || status.neutral;
  return (
    <Chip
      size="small"
      icon={icon}
      label={label}
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 700,
        bgcolor: t.bg,
        color: t.fg,
        "& .MuiChip-icon": { color: t.fg, fontSize: 14 },
      }}
    />
  );
};

/* ────────────────────── 1. the confidence ladder ────────────────────────── */

const TIERS = [
  { key: "stated", label: "Stated", field: "statedDscr", def: "Every row at face value." },
  { key: "validated", label: "Validated", field: "validatedDscr", def: "After each row's counted-% haircut." },
  { key: "verified", label: "Verified", field: "verifiedDscr", def: "Verified rows only — what the lock relies on." },
];
const same = (a, b) =>
  a != null && b != null && Math.abs(Number(a) - Number(b)) < 0.00005;

const ladderSteps = (calc) => {
  const steps = [];
  TIERS.forEach((t) => {
    const v = calc?.[t.field];
    const home = steps.find((s) => same(s.value, v));
    if (home) home.tiers.push(t);
    else steps.push({ value: v, tiers: [t] });
  });
  return steps;
};

const ConfidenceLadder = ({ calc, verifiedCount, sourceCount }) => {
  const steps = useMemo(() => ladderSteps(calc), [calc]);
  const pct = sourceCount ? Math.round((verifiedCount / sourceCount) * 100) : 0;
  return (
    <Panel sx={{ p: 2.5 }}>
      <Eyebrow>Debt service coverage · three confidences</Eyebrow>
      <Flx ac g={1.5} wrap sx={{ mt: 1.25 }}>
        {steps.map((s, i) => {
          const isVerified = s.tiers.some((t) => t.key === "verified");
          return (
            <Flx ac g={1.5} key={s.tiers.map((t) => t.key).join("=")}>
              {i > 0 ? <ArrowForwardRounded sx={{ fontSize: 18, color: slate[300] }} /> : null}
              <Tooltip
                title={
                  <Flx column g={0.5}>
                    {s.tiers.map((t) => (
                      <Txt key={t.key} sx={{ fontSize: 11.5 }}>
                        <b>{t.label}</b> — {t.def}
                      </Txt>
                    ))}
                  </Flx>
                }
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderRadius: "10px",
                    border: `1px solid ${isVerified ? teal[200] : slate[200]}`,
                    bgcolor: isVerified ? teal[50] : slate[50],
                    minWidth: 150,
                  }}
                >
                  <Txt sx={{ display: "block", fontSize: 11, fontWeight: 700, color: isVerified ? teal[700] : slate[600] }}>
                    {s.tiers.map((t) => t.label).join(" = ")}
                  </Txt>
                  <Txt
                    sx={{
                      display: "block",
                      fontSize: isVerified ? 34 : 26,
                      fontWeight: 800,
                      lineHeight: 1.1,
                      color: isVerified ? teal[800] : slate[900],
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {fmtX(s.value)}
                  </Txt>
                </Box>
              </Tooltip>
            </Flx>
          );
        })}
      </Flx>
      <Box sx={{ mt: 2 }}>
        <Flx ac jb g={1} sx={{ mb: 0.5 }}>
          <Txt sx={{ fontSize: 12, color: slate[600] }}>
            Verified rows {verifiedCount}/{sourceCount}
          </Txt>
          <Txt sx={{ fontSize: 12, color: slate[600] }}>{pct}%</Txt>
        </Flx>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: slate[100],
            "& .MuiLinearProgress-bar": { bgcolor: teal[500], borderRadius: 3 },
          }}
        />
        {calc?.fullyVerified === false ? (
          <Txt sx={{ display: "block", fontSize: 11.5, color: status.warning.fg, mt: 0.75 }}>
            Unverified rows are excluded from the verified DSCR until UW review.
          </Txt>
        ) : null}
      </Box>
    </Panel>
  );
};

/* ───────────────────────── 2. coverage + initial entry ──────────────────── */

const CoverageCard = ({ calc, onSaveFloor, saving }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const floor = calc?.programFloor;
  const verified = calc?.verifiedDscr;
  const clears = calc?.clearsFloor;
  const gate =
    clears == null
      ? { tone: "neutral", label: floor == null ? "Floor not set" : "Verified DSCR unknown", icon: <InfoOutlined /> }
      : clears
        ? { tone: "success", label: "Clears the floor — lock-ready", icon: <CheckCircleOutlined /> }
        : { tone: "error", label: "Below the floor — lock blocked", icon: <WarningAmberOutlined /> };
  // Gauge: floor sits at 50% of the bar; headroom either side scales to ±50% of the floor.
  const gaugePct =
    floor && verified != null
      ? Math.max(0, Math.min(100, 50 + ((verified - floor) / (floor * 0.5)) * 50))
      : null;
  const commit = (v) => {
    const n = v == null ? Number(draft) : v;
    if (!Number.isFinite(n)) return;
    onSaveFloor(n);
    setEditing(false);
  };
  return (
    <Panel sx={{ p: 2.5 }}>
      <Flx ac jb g={1}>
        <Eyebrow>Coverage · program floor</Eyebrow>
        <Pill label={gate.label} tone={gate.tone} icon={gate.icon} />
      </Flx>
      <Flx ac g={1} sx={{ mt: 1.25 }}>
        <Txt sx={{ fontSize: 26, fontWeight: 800, color: slate[900], fontVariantNumeric: "tabular-nums" }}>
          {fmtX(verified)}
        </Txt>
        <Txt sx={{ fontSize: 12.5, color: slate[500] }}>verified vs floor</Txt>
        {editing ? (
          <Flx ac g={0.5}>
            <TextField
              size="small"
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") setEditing(false);
              }}
              inputProps={{ inputMode: "decimal" }}
              sx={{ width: 84, "& .MuiInputBase-input": { fontSize: 12.5, py: 0.5 } }}
            />
            <Button size="small" onClick={() => commit()} disabled={saving}>Save</Button>
          </Flx>
        ) : (
          <Button
            size="small"
            variant="text"
            onClick={() => {
              setDraft(floor == null ? "" : String(floor));
              setEditing(true);
            }}
            sx={{ minWidth: 0, px: 0.75 }}
          >
            {floor == null ? "Set floor" : `floor ${fmtX(floor)} · edit`}
          </Button>
        )}
      </Flx>
      {editing ? (
        <Flx ac g={0.5} sx={{ mt: 0.75 }}>
          <Chip size="small" label="1.00 guideline" onClick={() => commit(1)} sx={{ height: 22, fontSize: 11 }} />
          <Chip size="small" label="0.80 exception" onClick={() => commit(0.8)} sx={{ height: 22, fontSize: 11 }} />
        </Flx>
      ) : null}
      {gaugePct != null ? (
        <Box sx={{ mt: 1.5, position: "relative", height: 10, borderRadius: 5, bgcolor: slate[100] }}>
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: `${gaugePct}%`,
              borderRadius: 5,
              bgcolor: clears ? status.success.solid : status.error.solid,
            }}
          />
          <Tooltip title={`Program floor ${fmtX(floor)}`}>
            <Box sx={{ position: "absolute", left: "50%", top: -3, bottom: -3, width: 2, bgcolor: slate[700] }} />
          </Tooltip>
        </Box>
      ) : null}
      {calc?.initialEntry ? (
        <Flx ac jb g={1} sx={{ mt: 1.75, pt: 1.25, borderTop: `1px dashed ${slate[200]}` }}>
          <Tooltip title="The DSCR as entered at loan creation, captured once on the workbook's first save. From then on the workbook's verified number writes to the loan.">
            <Txt sx={{ fontSize: 12, color: slate[600], cursor: "help" }}>Initial entry → workbook</Txt>
          </Tooltip>
          <Flx ac g={0.75}>
            <Txt sx={{ fontSize: 13, fontWeight: 700, color: slate[500] }}>{fmtX(calc.initialEntry.dscr)}</Txt>
            <ArrowForwardRounded sx={{ fontSize: 14, color: slate[400] }} />
            <Txt sx={{ fontSize: 13, fontWeight: 700, color: teal[800] }}>{fmtX(verified)}</Txt>
          </Flx>
        </Flx>
      ) : null}
      <Flx ac g={0.5} sx={{ mt: 1.5 }}>
        <LockOutlined sx={{ fontSize: 14, color: slate[400] }} />
        <Txt sx={{ fontSize: 11.5, color: slate[500] }}>
          At lock the verified DSCR overrides the requested figure and the full workbook snapshots onto the lock.
        </Txt>
      </Flx>
    </Panel>
  );
};

/* ────────────────────────── 3. the equation strip ───────────────────────── */

const Term = ({ label, value, sub, strong }) => (
  <Flx column g={0.25} sx={{ minWidth: 130 }}>
    <Txt sx={{ fontSize: 11, color: slate[500] }}>{label}</Txt>
    <Txt sx={{ fontSize: strong ? 22 : 18, fontWeight: 800, color: slate[900], fontVariantNumeric: "tabular-nums" }}>
      {value}
    </Txt>
    {sub ? <Txt sx={{ fontSize: 11, color: slate[500] }}>{sub}</Txt> : null}
  </Flx>
);
const Op = ({ children }) => (
  <Txt sx={{ fontSize: 22, fontWeight: 300, color: slate[400], px: 0.5 }}>{children}</Txt>
);

const EquationStrip = ({ calc, incomeValidated, expenseValidated, amortizationType }) => (
  <Panel
    title="How the validated DSCR is built"
    hint="Qualifying income ÷ PITIA. Income and expense rows are the validated (post-haircut) monthly figures; P&I is the loan's single canonical payment. All computed server-side."
  >
    <Flx ac g={1} wrap sx={{ px: 2.5, py: 2 }}>
      <Term label="Qualifying income / mo" value={formatDollar(incomeValidated, "—")} sub="validated income rows" />
      <Op>÷</Op>
      <Op>(</Op>
      <Term label="Expenses / mo" value={formatDollar(expenseValidated, "—")} sub="validated expense rows" />
      <Op>+</Op>
      <Term label="Monthly P&I" value={formatDollar(calc?.monthlyPiPayment, "—")} sub={dscrPaymentBasisLabel(calc, amortizationType)} />
      <Op>=</Op>
      <Term label="PITIA" value={formatDollar(calc?.pitiaValidated, "—")} />
      <Op>)</Op>
      <Op>=</Op>
      <Term label="Validated DSCR" value={fmtX(calc?.validatedDscr)} strong />
    </Flx>
  </Panel>
);

/* ─────────────────────────── 4. the grouped ledger ──────────────────────── */

/** Group row: property or unit, with income / expense subtotals from its leaves. */
const LedgerGroupRow = (p) => {
  const leaves = p.node?.allLeafChildren || [];
  let income = 0;
  let expense = 0;
  let verified = 0;
  leaves.forEach((n) => {
    const d = n.data || {};
    const v = Number(d.validatedMonthlyAmount) || 0;
    if (d.isIncome) income += v;
    else expense += v;
    if (d.verified) verified += 1;
  });
  const isProperty = p.node?.level === 0;
  return (
    <Flx ac jb g={2} sx={{ height: "100%", px: 1.5, pl: isProperty ? 1.5 : 4 }}>
      <Flx ac g={1}>
        <ExpandMoreRounded
          sx={{
            fontSize: 18,
            color: slate[500],
            transform: p.node?.expanded ? "none" : "rotate(-90deg)",
            transition: "transform 120ms",
          }}
        />
        <Txt sx={{ fontSize: isProperty ? 13 : 12.5, fontWeight: 700, color: slate[900] }}>
          {p.node?.key}
        </Txt>
        <Txt sx={{ fontSize: 11.5, color: slate[500] }}>
          {leaves.length} row{leaves.length === 1 ? "" : "s"} · {verified} verified
        </Txt>
      </Flx>
      <Flx ac g={2.5}>
        {income ? (
          <Txt sx={{ fontSize: 12, color: slate[600] }}>
            income <b>{formatDollar(income)}</b>
          </Txt>
        ) : null}
        {expense ? (
          <Txt sx={{ fontSize: 12, color: slate[600] }}>
            expenses <b>{formatDollar(expense)}</b>
          </Txt>
        ) : null}
      </Flx>
    </Flx>
  );
};

const LedgerGrid = ({ rows, docsById, activeSourceId, onEdit }) => {
  const dispatch = useDispatch();
  const columnDefs = useMemo(
    () => [
      { field: "groupProperty", rowGroup: true, hide: true },
      { field: "groupSub", rowGroup: true, hide: true },
      {
        headerName: "Row",
        colId: "row",
        flex: 1.5,
        minWidth: 240,
        valueGetter: (p) => p.data?.label || p.data?.componentType,
        cellRenderer: (p) => {
          const d = p.data || {};
          return (
            <Flx column g={0} sx={{ py: 0.5, minWidth: 0 }}>
              <Flx ac g={0.75}>
                <Txt sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {p.value}
                </Txt>
                {d.basisLabel ? (
                  <Chip size="small" label={d.basisLabel} sx={{ height: 17, fontSize: 10, fontWeight: 700, bgcolor: slate[100], color: slate[700], "& .MuiChip-label": { px: 0.75 } }} />
                ) : null}
                {d.aboveMarket ? (
                  <Tooltip title="Lease rent above appraisal market rent — qualifies in full with the two-months-of-rent-verified condition.">
                    <Chip size="small" label="lease > market" sx={{ height: 17, fontSize: 10, fontWeight: 700, bgcolor: amber.light, color: amber.chipText, "& .MuiChip-label": { px: 0.75 } }} />
                  </Tooltip>
                ) : null}
                {d.needsUnit ? (
                  <Tooltip title="Every rent row names a unit — open the row and pick one.">
                    <Chip size="small" label="no unit" sx={{ height: 17, fontSize: 10, fontWeight: 700, bgcolor: status.error.bg, color: status.error.fg, "& .MuiChip-label": { px: 0.75 } }} />
                  </Tooltip>
                ) : null}
              </Flx>
              <Txt sx={{ fontSize: 11, color: slate[500] }}>
                {d.componentType}
                {d.annual ? " · entered annually" : ""}
              </Txt>
            </Flx>
          );
        },
      },
      {
        headerName: "Entered",
        field: "amount",
        width: 120,
        type: "rightAligned",
        valueFormatter: (p) => (p.node?.group ? "" : formatDollar(p.value, "—")),
      },
      {
        headerName: "Monthly",
        field: "monthlyAmount",
        width: 110,
        type: "rightAligned",
        valueFormatter: (p) => (p.node?.group ? "" : formatDollar(p.value, "—")),
      },
      {
        headerName: "Counted",
        field: "countedPercentage",
        width: 95,
        type: "rightAligned",
        valueFormatter: (p) => (p.node?.group ? "" : `${p.value == null ? 100 : p.value}%`),
      },
      {
        headerName: "Validated / mo",
        field: "validatedMonthlyAmount",
        width: 130,
        type: "rightAligned",
        cellStyle: { fontWeight: 700 },
        valueFormatter: (p) => (p.node?.group ? "" : formatDollar(p.value, "—")),
      },
      {
        headerName: "Verified",
        colId: "verified",
        width: 95,
        cellRenderer: (p) =>
          p.node?.group ? null : (
            <Flx ac jc sx={{ height: "100%" }}>
              {p.data?.verified ? (
                <VerifiedOutlined sx={{ fontSize: 18, color: status.success.solid }} />
              ) : (
                <Tooltip title="Not yet verified — excluded from the verified DSCR">
                  <Box sx={{ width: 14, height: 14, borderRadius: "50%", border: `2px solid ${slate[300]}` }} />
                </Tooltip>
              )}
            </Flx>
          ),
      },
      {
        headerName: "Evidence",
        colId: "evidence",
        flex: 1,
        minWidth: 200,
        sortable: false,
        cellRenderer: (p) => {
          if (p.node?.group) return null;
          const ids = p.data?.loanDocumentIds || [];
          if (!ids.length)
            return (
              <Flx ac g={0.5} sx={{ height: "100%" }}>
                <WarningAmberOutlined sx={{ fontSize: 14, color: status.warning.fg }} />
                <Txt sx={{ fontSize: 11.5, color: status.warning.fg }}>No document</Txt>
              </Flx>
            );
          return (
            <Flx ac g={0.5} sx={{ height: "100%", flexWrap: "wrap", py: 0.5 }}>
              {ids.map((id) => {
                const doc = docsById.get(String(id));
                return (
                  <Chip
                    key={id}
                    size="small"
                    icon={<DescriptionOutlined sx={{ fontSize: 13 }} />}
                    label={doc?.file_display_name || doc?.fileName || doc?.docType || "Document"}
                    clickable={!!doc}
                    onClick={
                      doc
                        ? (e) => {
                            e.stopPropagation();
                            dispatch(filePreviewSetData(doc));
                          }
                        : undefined
                    }
                    sx={{ height: 20, fontSize: 11, maxWidth: 180 }}
                  />
                );
              })}
            </Flx>
          );
        },
      },
      actionColumn({
        colId: "edit",
        cellRenderer: (p) =>
          p.node?.group ? null : (
            <ActionCell>
              <Tooltip title="Open row">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(p.data);
                  }}
                >
                  <ArrowForwardRounded sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            </ActionCell>
          ),
      }),
    ],
    [docsById, dispatch, onEdit],
  );

  return (
    <AgGridTableWrapper compactTable noBorder suppressMinHeightWhenPopulated={rows}>
      <AgGridReact
        rowData={rows}
        columnDefs={columnDefs}
        defaultColDef={{ sortable: true, resizable: true, suppressHeaderMenuButton: true }}
        getRowId={(p) => String(p.data?._id)}
        domLayout="autoHeight"
        groupDisplayType="groupRows"
        groupRowRenderer={LedgerGroupRow}
        groupDefaultExpanded={-1}
        suppressAggFuncInHeader
        onRowClicked={(e) => {
          if (e.node?.group) e.node.setExpanded(!e.node.expanded);
          else onEdit(e.data);
        }}
        getRowStyle={(p) => ({
          cursor: "pointer",
          ...(p.node?.group
            ? { background: p.node.level === 0 ? slate[50] : "#fff", fontWeight: 600 }
            : String(p.data?._id) === String(activeSourceId)
              ? { background: teal[50] }
              : {}),
        })}
      />
    </AgGridTableWrapper>
  );
};

/* ─────────────────────── 5. component summary + evidence ────────────────── */

const ComponentGrid = ({ summary }) => {
  const columnDefs = useMemo(
    () => [
      { headerName: "Component", field: "componentType", flex: 1, minWidth: 170 },
      {
        headerName: "Side",
        field: "isIncome",
        width: 95,
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%" }}>
            <Pill label={p.value ? "Income" : "PITIA"} tone={p.value ? "success" : "info"} />
          </Flx>
        ),
      },
      { headerName: "Rows", field: "count", width: 70, type: "rightAligned" },
      { headerName: "Stated / mo", field: "stated", width: 120, type: "rightAligned", valueFormatter: (p) => formatDollar(p.value, "—") },
      { headerName: "Validated / mo", field: "validated", width: 130, type: "rightAligned", cellStyle: { fontWeight: 700 }, valueFormatter: (p) => formatDollar(p.value, "—") },
    ],
    [],
  );
  return (
    <AgGridTableWrapper compactTable noBorder suppressMinHeightWhenPopulated={summary}>
      <AgGridReact
        rowData={summary}
        columnDefs={columnDefs}
        defaultColDef={{ sortable: true, resizable: true, suppressHeaderMenuButton: true }}
        getRowId={(p) => p.data?.componentType}
        domLayout="autoHeight"
      />
    </AgGridTableWrapper>
  );
};

const AMOUNT_KEY = ["monthlyRent", "marketRentMonthly", "annualPremium", "annualTaxes"];

const EvidenceGrid = ({ evidence, usedBy, docsById }) => {
  const dispatch = useDispatch();
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Document",
        field: "fileName",
        flex: 1.3,
        minWidth: 220,
        cellRenderer: (p) => (
          <Flx ac g={0.75} sx={{ height: "100%", minWidth: 0 }}>
            <DescriptionOutlined sx={{ fontSize: 15, color: slate[500], flexShrink: 0 }} />
            <Txt sx={{ fontSize: 12.5, fontWeight: 600, textDecoration: "underline", textDecorationColor: slate[300], whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {p.value || p.data?.docType || "Document"}
            </Txt>
          </Flx>
        ),
      },
      { headerName: "Type", field: "docType", width: 170 },
      {
        headerName: "Evidences",
        field: "componentTypes",
        flex: 1,
        minWidth: 180,
        valueFormatter: (p) => (Array.isArray(p.value) ? p.value.join(", ") : ""),
        cellRenderer: (p) => (
          <Flx ac g={0.5} sx={{ height: "100%", flexWrap: "wrap" }}>
            {(p.value || []).map((c) => (
              <Chip key={c} size="small" label={c} sx={{ height: 18, fontSize: 10.5, bgcolor: slate[100] }} />
            ))}
            {p.data?.unitDesignator ? (
              <Chip size="small" label={`Unit ${p.data.unitDesignator}`} sx={{ height: 18, fontSize: 10.5, bgcolor: teal[100], color: teal[700] }} />
            ) : null}
          </Flx>
        ),
      },
      {
        headerName: "Says",
        colId: "value",
        width: 130,
        type: "rightAligned",
        valueGetter: (p) => {
          const c = p.data?.candidates || {};
          const k = AMOUNT_KEY.find((x) => c[x]?.value != null);
          return k ? Number(c[k].value) : null;
        },
        valueFormatter: (p) => formatDollar(p.value, "—"),
      },
      {
        headerName: "Used by",
        colId: "usedBy",
        width: 110,
        valueGetter: (p) => usedBy.get(String(p.data?.loanDocumentId)) || 0,
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%" }}>
            {p.value ? (
              <Pill label={`${p.value} row${p.value === 1 ? "" : "s"}`} tone="success" />
            ) : (
              <Pill label="not attached" tone="warning" />
            )}
          </Flx>
        ),
      },
      { headerName: "Status", field: "documentStatus", width: 110, valueFormatter: (p) => p.value || "—" },
    ],
    [usedBy],
  );
  return (
    <AgGridTableWrapper compactTable noBorder suppressMinHeightWhenPopulated={evidence}>
      <AgGridReact
        rowData={evidence}
        columnDefs={columnDefs}
        defaultColDef={{ sortable: true, resizable: true, suppressHeaderMenuButton: true }}
        getRowId={(p) => String(p.data?.loanDocumentId || p.data?.blobName)}
        domLayout="autoHeight"
        // A displayed file is always previewable — the row is the click target.
        onRowClicked={(e) => {
          const doc = docsById.get(String(e.data?.loanDocumentId));
          if (doc) dispatch(filePreviewSetData(doc));
        }}
        getRowStyle={() => ({ cursor: "pointer" })}
      />
    </AgGridTableWrapper>
  );
};

/* ───────────────────────── 6. change history timeline ───────────────────── */

const FIELD_LABEL = {
  componentType: "component", label: "label", amount: "amount", countedPercentage: "counted %",
  verified: "verified", notes: "notes", loanDocumentIds: "documents", unitId: "unit", rentBasis: "basis",
};
const fmtField = (f, v) => {
  if (v == null || v === "") return "—";
  if (f === "amount") return formatDollar(v, "—");
  if (f === "countedPercentage") return `${v}%`;
  if (f === "verified") return v ? "yes" : "no";
  if (f === "rentBasis") return rentBasisLabel(v) || String(v);
  if (f === "loanDocumentIds") return `${Array.isArray(v) ? v.length : 0} doc(s)`;
  if (f === "unitId") return `…${String(v).slice(-5)}`;
  return String(v);
};

const HistoryTimeline = ({ history }) => {
  const [open, setOpen] = useState(null);
  const entries = useMemo(
    () => [...(Array.isArray(history) ? history : [])].reverse(),
    [history],
  );
  if (!entries.length)
    return (
      <Txt sx={{ display: "block", fontSize: 12.5, color: slate[500], px: 2.5, py: 2 }}>
        No changes recorded yet — every save that changes a row lands here with who, when and the before → after.
      </Txt>
    );
  return (
    <Box sx={{ px: 2.5, py: 1.5 }}>
      {entries.map((e, i) => {
        const c = e?.changes || {};
        const parts = [];
        if (size(c.rows_changed)) parts.push(`${size(c.rows_changed)} changed`);
        if (size(c.rows_added)) parts.push(`${size(c.rows_added)} added`);
        if (size(c.rows_removed)) parts.push(`${size(c.rows_removed)} removed`);
        if (c.programFloor) parts.push(`floor ${fmtX(c.programFloor.old)} → ${fmtX(c.programFloor.new)}`);
        const isOpen = open === i;
        return (
          <Box key={i} sx={{ display: "grid", gridTemplateColumns: "14px 1fr", gap: 1.5, pb: 1.5 }}>
            <Flx column ac sx={{ pt: "5px" }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: teal[500], flexShrink: 0 }} />
              {i < entries.length - 1 ? <Box sx={{ flex: 1, width: 2, bgcolor: slate[200], mt: 0.5 }} /> : null}
            </Flx>
            <Box>
              <Flx ac jb g={1} onClick={() => setOpen(isOpen ? null : i)} sx={{ cursor: "pointer" }}>
                <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: slate[900] }}>{parts.join(" · ") || "save"}</Txt>
                <Txt sx={{ fontSize: 11, color: slate[500], whiteSpace: "nowrap" }}>
                  {e?.changedBy || "—"} · {when(e?.changedDateTime)}
                </Txt>
              </Flx>
              {isOpen ? (
                <Flx column g={0.5} sx={{ mt: 0.75, pl: 1, borderLeft: `2px solid ${slate[100]}` }}>
                  {(c.rows_changed || []).map((r, j) => (
                    <Txt key={`c${j}`} sx={{ fontSize: 12, color: slate[700] }}>
                      <b>{r?.row}</b>:{" "}
                      {Object.entries(r?.fields || {})
                        .map(([f, ch]) => `${FIELD_LABEL[f] || f} ${fmtField(f, ch?.old)} → ${fmtField(f, ch?.new)}`)
                        .join(" · ")}
                    </Txt>
                  ))}
                  {(c.rows_added || []).map((s, j) => (
                    <Txt key={`a${j}`} sx={{ fontSize: 12, color: status.success.fg }}>
                      + {s?.label || s?.componentType} · {formatDollar(s?.amount, "—")}
                    </Txt>
                  ))}
                  {(c.rows_removed || []).map((s, j) => (
                    <Txt key={`r${j}`} sx={{ fontSize: 12, color: status.error.fg }}>
                      − {s?.label || s?.componentType} · {formatDollar(s?.amount, "—")}
                    </Txt>
                  ))}
                </Flx>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

/* ────────────────────────────────── tab ─────────────────────────────────── */

const LoanDrilldownDscrWorkbookRedesignTab = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const loan = useSelector((s) => s.loanDrilldown);
  const calc = useSelector((s) => s.loanFetchedData?.dscrCalc);
  const components = useSelector((s) => s.loanFetchedData?.dscrCalcComponents);
  const evidence = useSelector((s) => s.loanFetchedData?.dscrCalcEvidence);
  const sidebarType = useSelector((s) => s.sidebar?.type);
  const sidebarState = useSelector((s) => s.sidebar?.state);
  const [savingFloor, setSavingFloor] = useState(false);

  const activeSourceId =
    sidebarType === loanDrilldownSidebarTypes.DSCR_ROW_REDESIGN
      ? sidebarState?._id || null
      : null;

  // The three reads — calc (seeded on first visit), catalog, evidence.
  useEffect(() => {
    if (!isNil(calc) || !loan?._id) return;
    dscrCalcApi
      .get(loan._id)
      .then((d) => dispatch(loanFetchedDataDscrCalcSet(d || {})))
      .catch((err) => {
        dispatch(loanFetchedDataDscrCalcSet({}));
        enqueueSnackbar(err?.response?.data?.detail || "Couldn't load the DSCR workbook", { variant: "error" });
      });
  }, [calc, loan?._id, dispatch, enqueueSnackbar]);
  useEffect(() => {
    if (components) return;
    dscrCalcApi
      .components()
      .then((d) => dispatch(loanFetchedDataDscrCalcComponentsSet(d || [])))
      .catch(() => dispatch(loanFetchedDataDscrCalcComponentsSet([])));
  }, [components, dispatch]);
  useEffect(() => {
    if (evidence != null || !loan?._id) return;
    dscrCalcApi
      .evidence(loan._id)
      .then((rows) => dispatch(loanFetchedDataDscrCalcEvidenceSet(rows || [])))
      .catch(() => dispatch(loanFetchedDataDscrCalcEvidenceSet([])));
  }, [evidence, loan?._id, dispatch]);

  const sources = useMemo(() => calc?.sources || [], [calc]);
  const docsById = useMemo(() => {
    const m = new Map();
    (loan?.loanDocuments || []).forEach((d) => m.set(String(d?._id), d));
    return m;
  }, [loan?.loanDocuments]);
  const unitsById = useMemo(
    () => new Map(listLoanUnits(loan).map((u) => [String(u.unitId), u])),
    [loan],
  );
  const propertyLabelById = useMemo(
    () => new Map((loan?.subjectProperties || []).map((sp, i) => [String(sp?._id), propertyLabelOf(sp, i)])),
    [loan?.subjectProperties],
  );

  // Ledger rows: the server's rows plus the grouping keys the model implies —
  // property → unit for rent, property → "Property expenses" for costs.
  const ledgerRows = useMemo(
    () =>
      sources.map((s) => {
        const income = isIncomeRow(s, components);
        const unit = s.unitId ? unitsById.get(String(s.unitId)) : null;
        const property =
          unit?.propertyLabel ||
          (s.subjectPropertyId ? propertyLabelById.get(String(s.subjectPropertyId)) : null) ||
          "Not tied to a property";
        const isRent = s.componentType === GROSS_MONTHLY_RENT;
        const meta = (components || []).find((c) => c?.componentType === s.componentType);
        return {
          ...s,
          isIncome: income,
          groupProperty: property,
          groupSub: unit ? `Unit ${unit.unitNumber}` : income ? "Other income" : "Property expenses",
          basisLabel: isRent ? rentBasisLabel(s.rentBasis) : null,
          aboveMarket: leaseAboveMarket(s, unit),
          needsUnit: isRent && !unit,
          annual: !!meta?.annual,
        };
      }),
    [sources, components, unitsById, propertyLabelById],
  );

  const incomeValidated = useMemo(
    () => ledgerRows.filter((r) => r.isIncome).reduce((a, r) => a + (Number(r.validatedMonthlyAmount) || 0), 0),
    [ledgerRows],
  );
  const expenseValidated = useMemo(
    () => ledgerRows.filter((r) => !r.isIncome).reduce((a, r) => a + (Number(r.validatedMonthlyAmount) || 0), 0),
    [ledgerRows],
  );
  const verifiedCount = useMemo(() => sources.filter((s) => s?.verified).length, [sources]);
  const usedBy = useMemo(() => {
    const m = new Map();
    sources.forEach((s) => (s?.loanDocumentIds || []).forEach((id) => m.set(String(id), (m.get(String(id)) || 0) + 1)));
    return m;
  }, [sources]);
  const evidenceRows = useMemo(
    () => (Array.isArray(evidence) ? evidence.filter((e) => e?.loanDocumentId) : []),
    [evidence],
  );

  // The redesign's own row drawer; `bucket` picks the income / expense anatomy.
  const openRow = useCallback(
    (source, bucket) => {
      const target = bucket || (isIncomeRow(source, components) ? "income" : "expense");
      dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.DSCR_ROW_REDESIGN));
      dispatch(sidebarSetStateValue({ ...(source || {}), bucket: target }));
    },
    [dispatch, components],
  );

  const saveFloor = useCallback(
    (programFloor) => {
      if (!loan?._id) return;
      setSavingFloor(true);
      dscrCalcApi
        .save(loan._id, { sources, programFloor })
        .then((d) => dispatch(loanFetchedDataDscrCalcSet(d)))
        .catch((err) => enqueueSnackbar(err?.response?.data?.detail || "Couldn't save the program floor", { variant: "error" }))
        .finally(() => setSavingFloor(false));
    },
    [loan?._id, sources, dispatch, enqueueSnackbar],
  );

  const refresh = () => {
    dispatch(loanFetchedDataDscrCalcSet(null));
    dispatch(loanFetchedDataDscrCalcEvidenceSet(null));
  };

  return (
    <Box sx={{ py: 3, px: 3, maxWidth: 1600, mx: "auto" }}>
      {/* WIP banner — this tab is reached from the "Redesign (WIP)" button on
          DSCR Calc., not from the nav, while it is being reviewed. */}
      <Flx
        ac
        jb
        g={2}
        wrap
        sx={{
          mb: 2.5,
          px: 2,
          py: 1.25,
          borderRadius: "10px",
          bgcolor: status.warning.bg,
          border: `1px solid ${amber.light}`,
        }}
      >
        <Flx ac g={1}>
          <WarningAmberOutlined sx={{ fontSize: 18, color: status.warning.fg }} />
          <Txt sx={{ fontSize: 12.5, color: status.warning.fg }}>
            <b>Work in progress</b> — a redesign of the DSCR Calculations tab,
            built from the backend contract. Same data, same saves; the layout
            is under review.
          </Txt>
        </Flx>
        <Button
          size="small"
          variant="text"
          onClick={() => navigate(`/loan/${loan?._id}/dscr-calculations`)}
        >
          Back to DSCR Calc.
        </Button>
      </Flx>
      {/* Header */}
      <Flx ac jb g={2} wrap sx={{ mb: 2.5 }}>
        <Box>
          <Txt sx={{ display: "block", fontSize: 18, fontWeight: 800, color: slate[900] }}>DSCR workbook</Txt>
          <Txt sx={{ fontSize: 12.5, color: slate[500] }}>
            Evidence, haircut and verification behind every number in the ratio. Rent rows belong to units; expense rows to the property.
          </Txt>
        </Box>
        <Flx ac g={0.75}>
          <Button size="small" variant="outlined" startIcon={<AddOutlined />} onClick={() => openRow(null, "income")}>
            Income row
          </Button>
          <Button size="small" variant="outlined" startIcon={<AddOutlined />} onClick={() => openRow(null, "expense")}>
            Expense row
          </Button>
          <Tooltip title="Reload the workbook and evidence">
            <IconButton size="small" onClick={refresh} disabled={isNil(calc)}>
              <SyncOutlined />
            </IconButton>
          </Tooltip>
        </Flx>
      </Flx>

      {/* 1 + 2 */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 3fr) minmax(0, 2fr)" }, gap: 2.5, mb: 2.5 }}>
        <ConfidenceLadder calc={calc} verifiedCount={verifiedCount} sourceCount={sources.length} />
        <CoverageCard calc={calc} onSaveFloor={saveFloor} saving={savingFloor} />
      </Box>

      {/* 3 */}
      <Box sx={{ mb: 2.5 }}>
        <EquationStrip
          calc={calc}
          incomeValidated={incomeValidated}
          expenseValidated={expenseValidated}
          amortizationType={loan?.amortizationType}
        />
      </Box>

      {/* 4 */}
      <Panel
        title={`Ledger · ${sources.length} row${sources.length === 1 ? "" : "s"}`}
        hint="Grouped by subject property, then unit. Group rows subtotal the validated monthly figures beneath them. Click a row to open it."
        sx={{ mb: 2.5 }}
        end={
          <Txt sx={{ fontSize: 11.5, color: slate[500] }}>
            Rows count as entered (after counted %); guideline caps are applied through the amount or the haircut.
          </Txt>
        }
      >
        {ledgerRows.length ? (
          <LedgerGrid rows={ledgerRows} docsById={docsById} activeSourceId={activeSourceId} onEdit={openRow} />
        ) : (
          <Flx column ac g={1} sx={{ py: 5 }}>
            <Txt sx={{ fontSize: 13, color: slate[600] }}>
              {isNil(calc) ? "Loading the workbook…" : "No rows yet. A DSCR loan seeds rows from its units and property on first open — add units on the Properties tab, or add a row here."}
            </Txt>
          </Flx>
        )}
      </Panel>

      {/* 5 */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(0, 3fr)" }, gap: 2.5, mb: 2.5 }}>
        <Panel title="By component" hint="Server-computed per-component monthly totals (componentSummary). Income feeds the numerator; PITIA components join P&I.">
          {size(calc?.componentSummary) ? (
            <ComponentGrid summary={calc.componentSummary} />
          ) : (
            <Txt sx={{ display: "block", fontSize: 12.5, color: slate[500], px: 2.5, py: 2 }}>Nothing to summarise yet.</Txt>
          )}
        </Panel>
        <Panel
          title={`Evidence on file · ${evidenceRows.length}`}
          hint="Loan documents with a structured extract the workbook can use (leases, appraisals, insurance, tax records). 'Not attached' means the document could evidence a row but no row uses it yet."
        >
          {evidenceRows.length ? (
            <EvidenceGrid evidence={evidenceRows} usedBy={usedBy} docsById={docsById} />
          ) : (
            <Txt sx={{ display: "block", fontSize: 12.5, color: slate[500], px: 2.5, py: 2 }}>
              No extracted documents yet. Once a lease, appraisal, insurance policy or tax record is parsed it appears here with the values it can supply.
            </Txt>
          )}
        </Panel>
      </Box>

      {/* 6 */}
      <Panel title={`Change history · ${size(calc?.changeHistory)}`} hint="Every save that changed something — who, when, and each row's before → after. No-change saves are not recorded.">
        <HistoryTimeline history={calc?.changeHistory} />
      </Panel>
    </Box>
  );
};

export default LoanDrilldownDscrWorkbookRedesignTab;
